import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import React, {useContext, useEffect, useState} from "react";
import FinanceStepper from "../../../component/FinanceStepper";
import {Button, Grid, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {ID_ASSISTED_DEBIT_NOTE, ID_CREDIT_NOTE, ID_DEBIT_NOTE} from "../constants/InvoicesTypes";
import {Box} from "@mui/system";
import {DoneAllRounded, DownloadRounded} from "@mui/icons-material";
import {FinanceStep} from "../../../constants/FinanceModels";
import InvoiceDetails from "../sections/InvoiceDetails";
import {ModifiedItem} from "../sections/InvoiceItems";
import {useInvoiceRepository} from "../../../api/repositories/InvoiceRepository";
import {ID_ADD_DAYS_NUMBER, ID_CHANGE_CHECKIN_DATE, ID_INVOICE_FULL_PERIOD} from "../constants/AssistedDebitNoteTypes";
import NumberInput from "../../../../../util/component/NumberInput";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {ID_OPEN} from "../constants/InvoicesStatuses";

const InvoiceNoteWizard = () => {

    const navigate = useNavigate();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const {t} = useTranslation('finance', {keyPrefix: 'invoices'})
    const {
        getInvoiceById,
        postCreditNote,
        postDebitNote,
        downloadPDF,
        enableAssistedDebitNote,
        rentingBoundsInfo,
        assistedDebitNoteValues
    } = useInvoiceRepository();


    const {id} = useParams();
    const [type, setType] = useState('');

    const [invoice, setInvoice] = useState<any>(undefined);
    const [previewInvoice, setPreviewInvoice] = useState<any>(undefined);

    const [modifiedItems, setModifiedItems] = useState<ModifiedItem[]>([]);
    const [createdNote, setCreatedNote] = useState<any>(undefined);
    const [createdNoteStatus, setCreatedNoteStatus] = useState<string>("");
    const [error, setError] = useState<boolean>(false);

    const [showAssistedDebitNote, setShowAssistedDebitNote] = useState<boolean>(true);
    const [showAssistedDebitNoteMode, setShowAssistedDebitNoteMode] = useState<boolean>(false);
    const [mode, setMode] = useState('');
    const [checkOutDate, setCheckOutDate] = useState('');
    const [checkInDate, setCheckInDate] = useState('');
    const [originCheckInDate, setOriginCheckInDate] = useState<string | undefined>(undefined);
    const [originCheckOutDate, setOriginCheckOutDate] = useState<string | undefined>(undefined);
    const [numberOfDays, setNumberOfDays] = useState(0);
    const [originNumberOfDays, setOriginNumberOfDays] = useState(0);

    const [debitNoteAssistedValues, setDebitNoteAssistedValues] = useState<any>(undefined);


    const types_open = [
        {
            value: ID_CREDIT_NOTE,
            label: t('note-variation-wizard.steps.types.' + ID_CREDIT_NOTE)
        },
        {
            value: ID_DEBIT_NOTE,
            label: t('note-variation-wizard.steps.types.' + ID_DEBIT_NOTE)
        }
    ];

    const types = [
        {
            value: ID_CREDIT_NOTE,
            label: t('types.' + ID_CREDIT_NOTE)
        },
        {
            value: ID_DEBIT_NOTE,
            label: t('types.' + ID_DEBIT_NOTE)
        },
        {
            value: ID_ASSISTED_DEBIT_NOTE,
            label: t('types.' + ID_ASSISTED_DEBIT_NOTE)
        }
    ];

    const modes = [
        {
            value: ID_INVOICE_FULL_PERIOD,
            label: t('modes.' + ID_INVOICE_FULL_PERIOD)
        },
        {
            value: ID_CHANGE_CHECKIN_DATE,
            label: t('modes.' + ID_CHANGE_CHECKIN_DATE)
        },
        {
            value: ID_ADD_DAYS_NUMBER,
            label: t('modes.' + ID_ADD_DAYS_NUMBER)
        }
    ];


    const stepSelectTypology = () => {
        return (
            <>
                <Grid item sx={{display: "flex", alignItems: "flex-inline"}}>
                    <>
                        <Grid item xs={2}>
                            <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}
                                        sx={{textTransform: 'uppercase'}}>{t('note-variation-wizard.steps.select-typology.title')}</Typography>
                            <TextField style={{width: "300px"}}
                                       select
                                       name="variation"
                                       value={type}
                                       label={type === '' ? t('note-variation-wizard.steps.select-typology.typology-label') : ' '}
                                       InputLabelProps={{shrink: false}}
                                       onChange={(event) => {
                                           setType(event.target.value);
                                           clearFields()
                                           setShowAssistedDebitNoteMode(event.target.value === ID_ASSISTED_DEBIT_NOTE)
                                       }}
                            >
                                {invoice && invoice.status === ID_OPEN ?
                                    types_open.map((option: any) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>

                                    )) :
                                    types.map((option: any) => (
                                        (option.value !== ID_ASSISTED_DEBIT_NOTE || option.value === ID_ASSISTED_DEBIT_NOTE && showAssistedDebitNote) &&
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>

                                    ))
                                }
                            </TextField>
                        </Grid>
                        {showAssistedDebitNoteMode && showModes()}
                        {(mode === ID_INVOICE_FULL_PERIOD || mode === ID_ADD_DAYS_NUMBER) && invoiceFullPeriod()}
                        {mode === ID_CHANGE_CHECKIN_DATE && changeCheckInDate()}
                        {mode === ID_ADD_DAYS_NUMBER && addNumberOfDays()}
                    </>
                </Grid>
            </>
        );
    }

    function clearFields() {
        setMode('')
        setCheckInDate('')
        setCheckOutDate('')
        setNumberOfDays(0)
    }

    const showModes = () => {
        return (
            <>
                <Grid item xs={3}>
                    <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}
                                sx={{textTransform: 'uppercase'}}>{t('note-variation-wizard.steps.select-assisted-mode.title')}</Typography>
                    <TextField style={{width: "250px"}}
                               select
                               name="mode"
                               value={mode}
                               label={mode === '' ? t('note-variation-wizard.steps.select-assisted-mode.assisted-mode-label') : ' '}
                               InputLabelProps={{shrink: false}}
                               onChange={(event) => {
                                   setMode(event.target.value);
                               }}
                    >
                        {modes.map((option: any) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </>
        )
    }


    const invoiceFullPeriod = () => {
        return (
            <>
                <Grid item xs={2}>
                    <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}>
                        {t('note-variation-wizard.steps.select-assisted-mode.checkoutTitle')}
                    </Typography>
                    <DateTimePicker
                        value={checkOutDate}
                        label={t("note-variation-wizard.steps.select-assisted-mode.checkoutTitle")}
                        mask={"__/__/____ __:__"}
                        inputFormat={"DD/MM/YYYY HH:mm"}
                        ampm={false}
                        disabled={true}
                        renderInput={(params: any) => (
                            <TextField
                                variant="standard"
                                fullWidth
                                {...params}
                                name="checkOutFullPeriod"
                            />
                        )}
                        onChange={(val: any) => setCheckOutDate(val)}
                    />
                </Grid>
                <Grid item xs={1}/>
                <Grid item xs={2}>
                    <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}>
                        {t('note-variation-wizard.steps.select-assisted-mode.checkinTitle')}
                    </Typography>
                    <DateTimePicker
                        value={checkInDate}
                        label={t("note-variation-wizard.steps.select-assisted-mode.checkinTitle")}
                        mask={"__/__/____ __:__"}
                        inputFormat={"DD/MM/YYYY HH:mm"}
                        ampm={false}
                        disabled={true}
                        renderInput={(params: any) => (
                            <TextField
                                variant="standard"
                                fullWidth
                                {...params}
                                name="checkInFullPeriod"
                                disabled={true}
                            />
                        )}
                        onChange={(val: any) => setCheckInDate(val)}
                    />
                </Grid>

            </>
        )
    }

    const changeCheckInDate = () => {
        return (
            <>
                <Grid item xs={2}>
                    <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}>
                        {t('note-variation-wizard.steps.select-assisted-mode.checkoutTitle')}
                    </Typography>
                    <DateTimePicker
                        value={checkOutDate}
                        label={t("note-variation-wizard.steps.select-assisted-mode.checkoutTitle")}
                        mask={"__/__/____ __:__"}
                        inputFormat={"DD/MM/YYYY HH:mm"}
                        ampm={false}
                        disabled={true}
                        renderInput={(params: any) => (
                            <TextField
                                variant="standard"
                                fullWidth
                                {...params}
                                name="checkOutFullPeriod"
                            />
                        )}
                        onChange={(val: any) => setCheckOutDate(val)}
                    />
                </Grid>
                <Grid item xs={1}/>
                <Grid item xs={2}>
                    <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}>
                        {t('note-variation-wizard.steps.select-assisted-mode.checkinTitle')}
                    </Typography>
                    <DateTimePicker
                        value={checkInDate}
                        label={t("note-variation-wizard.steps.select-assisted-mode.checkinTitle")}
                        mask={"__/__/____ __:__"}
                        inputFormat={"DD/MM/YYYY HH:mm"}
                        ampm={false}
                        minDateTime={dayjs(originCheckOutDate)}
                        maxDateTime={dayjs(originCheckInDate)}
                        renderInput={(params: any) => (
                            <TextField
                                variant="standard"
                                fullWidth
                                {...params}
                                name="checkInChangePeriod"
                            />
                        )}
                        onChange={(val: any) => {
                            setCheckInDate(val);
                            checkDebitNoteAssistedValidation();
                        }}
                    />
                </Grid>

            </>
        )
    }

    const addNumberOfDays = () => {
        return (
            <>
                <Grid item xs={1}/>
                <Grid item xs={1}>
                    <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}
                                style={{marginBottom: "-13px"}}>
                        {t('note-variation-wizard.steps.select-assisted-mode.numberOfDaysTitle')}
                    </Typography>
                    <NumberInput
                        label={''}
                        minValue={1}
                        maxValue={originNumberOfDays}
                        defaultValue={originNumberOfDays}
                        onChange={(value: number) => {
                            if (checkInDate != '') {
                                let newDate = dayjs(checkOutDate).add(value, 'day').toDate();
                                setCheckInDate(calculateMinDate(originCheckInDate, newDate));
                                checkDebitNoteAssistedValidation();
                                setNumberOfDays(value)
                            }
                        }}
                    />
                </Grid>
            </>
        )
    }

    const stepEditItems = () => {
        return (
            <>

                <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}
                            sx={{textTransform: 'uppercase'}}>{t('note-variation-wizard.steps.edit-items.title')}</Typography>
                <InvoiceDetails invoice={previewInvoice} originalInvoice={invoice} editable={true} preview={false}
                                type={type} modifiedItems={modifiedItems}
                                debitNoteAssistedValues={debitNoteAssistedValues}
                                setModifiedItems={setModifiedItems}
                                setError={() => {
                                    if (!error) {
                                        setError(true)
                                    }
                                }}
                                setSuccess={() => {
                                    if (error) {
                                        setError(false)
                                    }
                                }}
                />
            </>
        );
    }
    const stepPreview = () => {
        return (
            <>
                <Typography variant={'body1'} color={'primary.light'}
                            fontWeight={500}>{t('note-variation-wizard.steps.preview.subtitle')}</Typography>
                <Typography variant={'body2'} color={'primary.light'} fontWeight={700}
                            sx={{textTransform: 'uppercase'}}>{t('note-variation-wizard.steps.preview.title')}</Typography>
                <InvoiceDetails invoice={previewInvoice} modifiedItems={modifiedItems} editable={false} preview={true}
                                type={type}/>
            </>
        );
    }

    const stepEnd = () => {
        return (
            <Box sx={{marginTop: '10%', display: 'flex', justifyContent: 'center', justifyItems: 'center'}}>
                <Box sx={{width: 'fit-content', display: 'flex', flexFlow: 'column', alignItems: 'center'}}>
                    {(createdNoteStatus !== undefined && createdNoteStatus !== null && createdNoteStatus === 'OK') ?
                        <>
                            <DoneAllRounded sx={{color: '#00A0DF'}}/>
                            <>
                                <Typography variant={'h6'} textAlign={'center'}>
                                    {t('note-variation-wizard.steps.end.modal.title')}
                                </Typography>
                                <Typography variant={'body1'} textAlign={'center'}
                                            style={{whiteSpace: "pre-line"}}>
                                    {createdNote?.status === 'OPEN' ?
                                        t('note-variation-wizard.steps.end.modal.content-open', {invoiceId: createdNote?.invoiceNumber}) :
                                        t('note-variation-wizard.steps.end.modal.content', {invoiceId: createdNote?.invoiceNumber})
                                    }
                                </Typography>
                                <Stack sx={{margin: '10px'}} direction={'row'} spacing={5}>
                                    <Button variant={'outlined'} color={'accent'}
                                            startIcon={<DownloadRounded/>} onClick={() => {
                                        downloadPDF(createdNote?.id).then((res) => {
                                            if (res?.result) {
                                                let pdfWindow = window.open("");
                                                pdfWindow!.document.write(`<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(res.result)}'></iframe>`);
                                            }
                                        });
                                    }}>
                                        {t('note-variation-wizard.steps.end.download-button')}
                                    </Button>
                                    <Button variant={'contained'} color={'accent'}
                                            onClick={() => {
                                                navigate(`/finance/invoices/${createdNote?.id}`);
                                            }}>
                                        {createdNote?.status === 'OPEN' ? t('note-variation-wizard.steps.end.back-button-open') : t('note-variation-wizard.steps.end.back-button')}
                                    </Button>
                                </Stack>
                            </>

                        </>
                        :
                        <>
                            {(createdNoteStatus !== undefined && createdNoteStatus !== null && createdNoteStatus === 'KO') &&
                                <>
                                    <Typography variant={'h6'} textAlign={'center'}>
                                        {t('note-variation-wizard.steps.end.residueExceeded')}
                                    </Typography>
                                </>
                            }
                        </>
                    }
                </Box>
            </Box>
        )
            ;
    }

    const steps: FinanceStep[] = [
        {
            label: t('note-variation-wizard.steps.select-typology.title'),
            content:
                stepSelectTypology(),
            actionPreviousTitle:
                t('note-variation-wizard.steps.buttons.back-detail'),
            actionContinueTitle:
                t('note-variation-wizard.steps.buttons.continue'),
            actionPreviousCallback:
                () => navigate(`/finance/invoices/${id}`),
            actionContinueAccessible:
                checkDebitNoteAssistedValidation()
        },
        {
            label: t('note-variation-wizard.steps.edit-items.title'),
            content:
                stepEditItems(),
            actionPreviousTitle:
                t('note-variation-wizard.steps.buttons.back'),
            actionContinueTitle:
                t('note-variation-wizard.steps.buttons.continue'),
            actionContinueAccessible:
                modifiedItems.length !== 0 && !error
        },
        {
            label: t('note-variation-wizard.steps.preview.title'),
            content:
                stepPreview(),
            actionPreviousTitle:
                t('note-variation-wizard.steps.buttons.back-edit'),
            actionContinueTitle:
                t('note-variation-wizard.steps.buttons.complete'),
            actionContinueCallback:
                () => {
                    if (type === ID_CREDIT_NOTE) {
                        postCreditNote(invoice.id, modifiedItems.map(it => {
                            return {
                                parentId: it.parentId,
                                quantity: it.quantity,
                                totalPrice: it.amount !== undefined ? Math.round(it.amount * 100) : it.amount
                            };
                        }), invoice.legalEntity, invoice.dealer).then(it => {
                            console.log("response ", it);
                            console.log("status ", it.status);
                            setCreatedNote(it.result);
                            setCreatedNoteStatus(it.status);
                        });
                    } else if (type === ID_DEBIT_NOTE || ID_ASSISTED_DEBIT_NOTE) {
                        postDebitNote(invoice.id, modifiedItems.map(it => {
                            return {
                                parentId: it.parentId,
                                quantity: it.quantity,
                                totalPrice: it.amount !== undefined ? Math.round(it.amount * 100) : it.amount
                            };
                        }), invoice.legalEntity, invoice.dealer).then(it => {
                            setCreatedNote(it.result);
                            setCreatedNoteStatus(it.status);
                        });
                    }
                }
        },
        {
            label: t('note-variation-wizard.steps.end.title'),
            content:
                stepEnd(),
        },
    ];

    function checkDebitNoteAssistedValidation() {
        if (type === ID_ASSISTED_DEBIT_NOTE) {
            var checkInDateFormatted = dayjs(checkInDate).format("DD/MM/YYYY HH:mm")
            var checkOutDateFormatted = dayjs(checkOutDate).format("DD/MM/YYYY HH:mm")

            if (mode === ID_ADD_DAYS_NUMBER) {
                return numberOfDays != 0 && numberOfDays <= originNumberOfDays
            } else if (!isNaN(dayjs(checkInDate).date())) {
                return checkInDateFormatted != checkOutDateFormatted
                    && dayjs(checkInDate).isAfter(dayjs(checkOutDate))
                    && (dayjs(checkInDate).isBefore(dayjs(originCheckInDate)) || dayjs(checkInDate).isSame(dayjs(originCheckInDate)))
            } else return false;

        } else
            return type != ''
    }

    function calculateMinDate(originDate: any, updatedDate: any) {
        if (dayjs(originDate).isBefore(dayjs(updatedDate))) {
            return originDate;
        } else {
            return updatedDate;
        }
    }

    useEffect(() => {
        if (id !== undefined) {
            getInvoiceById(id).then((data) => {
                setInvoice(data.result);

                let invoiceClone = {...data.result};
                invoiceClone.vatRate = [];
                invoiceClone.totalNetPrice = 0;
                invoiceClone.totalVat = 0;
                invoiceClone.totalGrossPrice = 0;
                setPreviewInvoice(invoiceClone);
            });
            enableAssistedDebitNote(id).then((data) => {
                setShowAssistedDebitNote(data.visibility === "SHOW")
            })
        }
    }, [])

    useEffect(() => {
        const ntwRaData = invoice?.items?.find((it: any) => it.ntwRaData && it.ntwRaData.periodId)?.ntwRaData;
        if (ntwRaData?.periodId != null) {
            rentingBoundsInfo(ntwRaData.periodId).then((data) => {
                if (data.message === null) {
                    setCheckOutDate(data.company.endDate);
                    setCheckInDate(data.effective.endDate);
                    setOriginCheckOutDate(data.company.endDate);
                    setOriginCheckInDate(data.effective.endDate);

                    if (mode === ID_ADD_DAYS_NUMBER) {
                        setNumberOfDays(data.effective.duration - data.company.duration)
                        setOriginNumberOfDays(data.effective.duration - data.company.duration)
                    }
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('note-variation-wizard.steps.pricing.message' + data.message),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true
                            }
                        }
                    });
                }
            })
        }
    }, [mode])

    useEffect(() => {
        if (previewInvoice !== undefined) {
            let previewInvoiceClone = {...previewInvoice};

            previewInvoiceClone.items = [];
            previewInvoiceClone.vatRate = [];
            previewInvoiceClone.totalNetPrice = 0;
            previewInvoiceClone.totalVat = 0;
            previewInvoiceClone.totalGrossPrice = 0;

            invoice.items.forEach((it: any) => {
                let itemClone = {...it};
                let modifiedItem = modifiedItems.find(mit => mit.parentId === it.id);
                if (modifiedItem !== undefined) {
                    if (modifiedItem.quantity !== undefined) {
                        itemClone.quantity = modifiedItem.quantity;

                        let newGross = Math.round(it.totalGrossPrice * modifiedItem.quantity / it.quantity);
                        let newNet = Math.round(newGross / (1.00 + (it.taxType.value / 100)));
                        let newVat = newGross - newNet;

                        itemClone.totalNetPrice = newNet
                        itemClone.totalVat = newVat
                        itemClone.totalGrossPrice = newGross
                    }
                    if (modifiedItem.amount !== undefined) {
                        itemClone.quantity = undefined;
                        itemClone.totalNetPrice = modifiedItem.amount * 100;
                        itemClone.totalVat = itemClone.totalNetPrice * itemClone.taxType.value / 100;
                        itemClone.totalGrossPrice = itemClone.totalNetPrice + itemClone.totalVat;
                    }
                    previewInvoiceClone.items.push(itemClone);

                    let vatItem: any = previewInvoiceClone.vatRate.find((vit: any) => vit.rate === itemClone.taxType.value);
                    if (vatItem) {
                        vatItem.totalNetPrice += itemClone.totalNetPrice;
                        vatItem.totalVat += itemClone.totalVat;
                        vatItem.totalGrossPrice += itemClone.totalGrossPrice;
                    } else {
                        let newVatItem = {
                            rate: itemClone.taxType.value,
                            totalNetPrice: itemClone.totalNetPrice,
                            totalVat: itemClone.totalVat,
                            totalGrossPrice: itemClone.totalGrossPrice
                        };
                        previewInvoiceClone.vatRate.push(newVatItem);
                    }

                    previewInvoiceClone.totalNetPrice += itemClone.totalNetPrice;
                    previewInvoiceClone.totalVat += itemClone.totalVat;
                    previewInvoiceClone.totalGrossPrice += itemClone.totalGrossPrice;

                } else {
                    previewInvoiceClone.items.push(itemClone)
                }
            });

            setPreviewInvoice(previewInvoiceClone);
        }
    }, [modifiedItems])

    useEffect(() => {
        if (checkInDate !== "" && !isNaN(dayjs(checkInDate).date())) {
            assistedDebitNoteValues(id, checkInDate).then((res) => {
                if (res.message === undefined) {
                    setDebitNoteAssistedValues(res.values);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('note-variation-wizard.steps.' + res.message),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true
                            }
                        }
                    });
                }

            });
        }
    }, [checkInDate])

    return (
        <FinanceStepper steps={steps}/>
    );
}

export default InvoiceNoteWizard;